import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useOutletContext } from 'react-router-dom';
import { apiUrl } from '../utils/apiConfig';
import { LINKS } from '../constants/links';
import { PREFETCH_KEYS, readPrefetch } from '../utils/preloadApp';
import {
    clearSchedulePrefs,
    readSchedulePrefs,
    scheduleAccountId,
    writeSchedulePrefs,
} from '../utils/schedulePrefs';
import timeIcon from '../assets/time-icon-192.webp';
import BannerCarousel from './BannerCarousel';
import RoomMapModal from './RoomMapModal';
import StickmanRunner from './StickmanRunner';
import SignalLoader from './SignalLoader';
import SmileLoader from './SmileLoader';
import CupSteamLoader from './CupSteamLoader';
import WelcomeBuddy from './WelcomeBuddy';
import './styles/Schedule.css';

/** Аудитория «дист» — дистанционное занятие без кабинета */
const isDistanceRoom = (room) => {
    const t = String(room || '')
        .trim()
        .toLowerCase()
        .replace(/\./g, '');
    return t === 'дист' || t === 'дистанционно' || t === 'дистант';
};

const lessonStartTimes = {
    1: ['08:30', '09:50', '11:30', '13:00', '14:20', '15:40', '17:00'],
    2: ['08:30', '09:50', '11:30', '13:00', '14:20', '15:40', '17:00'],
    3: ['08:30', '09:50', '11:30', '13:00', '14:20', '15:40', '17:00'],
    4: ['08:30', '09:40', '11:10', '12:30', '14:40', '15:50', '17:00'],
    5: ['08:30', '09:50', '11:30', '13:00', '14:20', '15:40', '17:00'],
    6: ['08:30', '09:50', '11:30', '13:00', '14:20', '15:40', '17:00'],
};

const MONTH_NAMES = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const toIsoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** В воскресенье смотрим уже понедельник следующей недели. */
const displayScheduleDate = (now = new Date()) => {
    const d = new Date(now);
    d.setHours(12, 0, 0, 0);
    if (d.getDay() === 0) {
        d.setDate(d.getDate() + 1);
    }
    return d;
};

const displayWeekDayNumber = (now = new Date()) => {
    const day = displayScheduleDate(now).getDay();
    return day === 0 ? 1 : day;
};

const parseIsoDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12, 0, 0);
};

const isoWeekdayFromIso = (iso) => {
    const d = parseIsoDate(iso);
    if (!d) return 1;
    const js = d.getDay();
    return js === 0 ? 7 : js;
};

const formatRuDate = (iso) => {
    const d = parseIsoDate(iso);
    if (!d) return '';
    return d.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const buildMonthCells = (year, month) => {
    const first = new Date(year, month, 1, 12);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i += 1) {
        const d = new Date(year, month, 1 - startOffset + i, 12);
        cells.push({ iso: toIsoDate(d), day: d.getDate(), inMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        const d = new Date(year, month, day, 12);
        cells.push({ iso: toIsoDate(d), day, inMonth: true });
    }
    while (cells.length % 7 !== 0) {
        const last = parseIsoDate(cells[cells.length - 1].iso);
        const d = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1, 12);
        cells.push({ iso: toIsoDate(d), day: d.getDate(), inMonth: false });
    }
    return cells;
};

const SearchableSelector = ({
    value,
    onChange,
    options = [],
    placeholder = '',
    disabled = false,
    icon = 'bi-search',
    label = '',
}) => {
    const [query, setQuery] = useState(value || '');
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!rootRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const filtered = options.filter((opt) =>
        String(opt).toLowerCase().includes(String(query || '').toLowerCase())
    );

    const pick = (opt) => {
        onChange(opt);
        setQuery(opt);
        setOpen(false);
    };

    return (
        <div className="searchable-selector-container" ref={rootRef}>
            {label ? (
                <label className="selector-label">
                    <i className={`bi ${icon}`}></i>
                    {label}
                </label>
            ) : null}
            <div className="searchable-selector-wrapper">
                <div className="search-input-container">
                    <input
                        type="text"
                        className="searchable-selector-input"
                        value={query}
                        disabled={disabled}
                        placeholder={placeholder}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                            if (value) onChange('');
                        }}
                        onFocus={() => setOpen(true)}
                        autoComplete="off"
                    />
                    {query ? (
                        <button
                            type="button"
                            className="clear-search-btn"
                            onClick={() => {
                                setQuery('');
                                onChange('');
                                setOpen(true);
                            }}
                            aria-label="Очистить"
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="dropdown-toggle-btn"
                        disabled={disabled}
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Открыть список"
                    >
                        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`}></i>
                    </button>
                </div>
                {open && !disabled ? (
                    <div className="searchable-dropdown" role="listbox">
                        {filtered.length === 0 ? (
                            <div className="dropdown-no-results">Ничего не найдено</div>
                        ) : (
                            filtered.map((opt) => (
                                <div
                                    key={opt}
                                    role="option"
                                    aria-selected={opt === value}
                                    className={`dropdown-option ${opt === value ? 'selected' : ''}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        pick(opt);
                                    }}
                                >
                                    <span>{opt}</span>
                                    {opt === value ? <i className="bi bi-check-lg"></i> : null}
                                </div>
                            ))
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const Schedule = () => {
    const { openExternal, user } = useOutletContext() || {};
    const accountId = user?.id || user?.username || scheduleAccountId();
    const [schedule, setSchedule] = useState([]);
    const [selectedDay, setSelectedDay] = useState(() => displayWeekDayNumber());
    const [viewDay, setViewDay] = useState(() => displayWeekDayNumber());
    const [loading, setLoading] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [error, setError] = useState(null);
    const [screenshotLoading, setScreenshotLoading] = useState(false);

    const [scheduleType, setScheduleType] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [groups, setGroups] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [rememberChoice, setRememberChoice] = useState(false);
    const [horizon, setHorizon] = useState(() => readSchedulePrefs().horizon);
    const [selectedDate, setSelectedDate] = useState(() => toIsoDate(displayScheduleDate()));
    const [calCursor, setCalCursor] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });
    const [monthDays, setMonthDays] = useState({});
    const [periodMeta, setPeriodMeta] = useState({ start: null, end: null });
    const [loadingData, setLoadingData] = useState(true);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [introDismissed, setIntroDismissed] = useState(false);
    const [uiReveal, setUiReveal] = useState(false);
    const [drawerDragX, setDrawerDragX] = useState(0);
    const [drawerDragging, setDrawerDragging] = useState(false);
    const [mapRoom, setMapRoom] = useState(null);
    const [daysDocked, setDaysDocked] = useState(false);
    const [daySlideDir, setDaySlideDir] = useState(0); // 1 = вперёд (вправо→), -1 = назад
    const [nowTick, setNowTick] = useState(() => Date.now());

    const scheduleRef = useRef(null);
    const daysNavRef = useRef(null);
    const settingsPanelRef = useRef(null);
    const loadReqRef = useRef(0);
    const hasLoadedRef = useRef(false);
    const drawerSwipeRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        dx: 0,
        axis: null,
    });
    const drawerCloseTimerRef = useRef(null);

    useEffect(() => {
        loadGroupsAndTeachers();
        loadScheduleMeta();
    }, []);

    useEffect(() => {
        const prefs = readSchedulePrefs();
        if (prefs.type) {
            setScheduleType(prefs.type);
            setRememberChoice(true);
            setSelectedGroup(prefs.type === 'student' ? prefs.group : '');
            setSelectedTeacher(prefs.type === 'teacher' ? prefs.teacher : '');
        } else {
            setScheduleType('');
            setSelectedGroup('');
            setSelectedTeacher('');
            setRememberChoice(false);
            setIntroDismissed(false);
            setSchedule([]);
            setError(null);
            hasLoadedRef.current = false;
            loadReqRef.selectionKey = '';
        }
        setHorizon(prefs.horizon || 'week');
    }, [accountId]);

    useEffect(() => {
        const id = window.setInterval(() => setNowTick(Date.now()), 30000);
        return () => window.clearInterval(id);
    }, []);

    const loadGroupsAndTeachers = async () => {
        const cachedGroups = readPrefetch(PREFETCH_KEYS.groups);
        const cachedTeachers = readPrefetch(PREFETCH_KEYS.teachers);

        if (Array.isArray(cachedGroups) && Array.isArray(cachedTeachers)) {
            setGroups(cachedGroups);
            setTeachers(cachedTeachers);
            setLoadingData(false);
            return;
        }

        setLoadingData(true);
        try {
            const groupsResponse = await axios.get(
                apiUrl('/api/public/groups'),
                { timeout: 10000 }
            );

            const teachersResponse = await axios.get(
                apiUrl('/api/public/teachers'),
                { timeout: 10000 }
            );

            setGroups(groupsResponse.data || []);
            setTeachers(teachersResponse.data || []);
        } catch (err) {
            console.error('Ошибка загрузки данных:', err);
            const fallbackGroups = ['ИСП-20-1', 'ИСП-20-2', 'ИСП-21-1', 'ИСП-21-2', 'ПРОГ-20-1'];
            const fallbackTeachers = ['Иванов И.И.', 'Петрова А.С.', 'Сидоров В.В.', 'Кузнецова О.П.'];
            setGroups(fallbackGroups);
            setTeachers(fallbackTeachers);
            console.warn('Используются fallback данные');
        } finally {
            setLoadingData(false);
        }
    };

    const loadScheduleMeta = async () => {
        try {
            const resp = await axios.get(apiUrl('/api/public/schedule/meta'), { timeout: 8000 });
            setPeriodMeta({
                start: resp.data?.period_start || null,
                end: resp.data?.period_end || null,
            });
        } catch (err) {
            setPeriodMeta({ start: null, end: null });
        }
    };

    const getLessonNumberByTime = (startTime, dayOfWeek) => {
        if (!startTime || !dayOfWeek) return null;
        const normalizedTime = startTime.toString().trim().replace(/\s/g, '').substring(0, 5);
        const dayTimes = lessonStartTimes[dayOfWeek];
        if (!dayTimes) return null;
        const lessonIndex = dayTimes.findIndex((time) => time === normalizedTime);
        return lessonIndex !== -1 ? lessonIndex + 1 : null;
    };

    const loadSchedule = async () => {
        if (!scheduleType) {
            setError('Выберите тип расписания');
            return;
        }
        if (scheduleType === 'student' && !selectedGroup) {
            setError('Выберите группу');
            return;
        }
        if (scheduleType === 'teacher' && !selectedTeacher) {
            setError('Выберите преподавателя');
            return;
        }

        const reqId = ++loadReqRef.current;
        const selectionKey = `${scheduleType}|${selectedGroup}|${selectedTeacher}`;
        const isSwitch = hasLoadedRef.current;
        if (isSwitch) {
            setSwitching(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            let url = '';
            if (horizon === 'semester') {
                if (scheduleType === 'student') {
                    url = apiUrl(`/api/public/schedule/students/${selectedGroup}/date/${selectedDate}`);
                } else {
                    const encodedTeacherName = encodeURIComponent(selectedTeacher);
                    url = apiUrl(`/api/public/schedule/teachers/${encodedTeacherName}/date/${selectedDate}`);
                }
            } else if (scheduleType === 'student') {
                url = apiUrl(`/api/public/schedule/students/${selectedGroup}/${selectedDay}`);
            } else {
                const encodedTeacherName = encodeURIComponent(selectedTeacher);
                url = apiUrl(`/api/public/schedule/teachers/${encodedTeacherName}/${selectedDay}`);
            }

            const response = await axios.get(url, { timeout: 10000 });
            if (reqId !== loadReqRef.current) return;

            if (response.status === 200) {
                setSchedule(response.data);
                const nextViewDay =
                    horizon === 'semester'
                        ? isoWeekdayFromIso(selectedDate)
                        : selectedDay;
                setViewDay(nextViewDay);
                hasLoadedRef.current = true;
                loadReqRef.selectionKey = selectionKey;

                if (rememberChoice) {
                    writeSchedulePrefs({
                        type: scheduleType,
                        horizon,
                        group: scheduleType === 'student' ? selectedGroup : '',
                        teacher: scheduleType === 'teacher' ? selectedTeacher : '',
                    });
                }
            }
        } catch (err) {
            if (reqId !== loadReqRef.current) return;
            console.error('Ошибка загрузки расписания:', err);
            const status = err.response?.status;
            const isNetwork =
                !err.response ||
                err.code === 'ERR_NETWORK' ||
                err.code === 'ECONNABORTED' ||
                err.code === 'NETWORK_ERROR' ||
                /network|timeout|failed to fetch|err_connection/i.test(
                    String(err.message || '')
                );

            if (status === 404) {
                setError('Расписание не найдено');
            } else if (status === 400) {
                setError('Неверные параметры запроса');
            } else if (isNetwork || (status && status >= 500)) {
                setError('Проблемы с сетью');
            } else {
                setError('Не удалось загрузить расписание');
            }
        } finally {
            if (reqId === loadReqRef.current) {
                setLoading(false);
                setSwitching(false);
            }
        }
    };

    const resetSelection = () => {
        setScheduleType('');
        setSelectedGroup('');
        setSelectedTeacher('');
        setSchedule([]);
        setError(null);
        setRememberChoice(false);
        setIntroDismissed(false);
        hasLoadedRef.current = false;
        loadReqRef.selectionKey = '';
        clearSchedulePrefs();
    };

    const handleDayChange = (day) => {
        if (day === selectedDay) return;
        setDaySlideDir(day > selectedDay ? 1 : -1);
        setSelectedDay(day);
    };

    useEffect(() => {
        if (
            scheduleType &&
            ((scheduleType === 'student' && selectedGroup) ||
                (scheduleType === 'teacher' && selectedTeacher))
        ) {
            loadSchedule();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, selectedDate, selectedGroup, selectedTeacher, scheduleType, horizon]);

    const takeScreenshot = async () => {
        if (!scheduleRef.current || screenshotLoading) return;
        setScreenshotLoading(true);
        try {
            const canvas = await html2canvas(scheduleRef.current, {
                backgroundColor: '#ffffff',
                scale: Math.min(2, window.devicePixelRatio || 1.5),
                useCORS: true,
                logging: false,
            });
            const link = document.createElement('a');
            const who =
                scheduleType === 'student'
                    ? selectedGroup
                    : (selectedTeacher || 'schedule').replace(/\s+/g, '_');
            link.download = `kkt-schedule-${who}-day${selectedDay}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Ошибка скриншота:', err);
            setError('Не удалось сделать скриншот');
        } finally {
            setScreenshotLoading(false);
        }
    };

    const getDayName = (dayNumber) => {
        const days = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
        return days[dayNumber - 1];
    };

    const getDateForDay = (dayNumber) => {
        const now = displayScheduleDate();
        const jsDay = now.getDay();
        const mondayOffset = jsDay === 0 ? -6 : 1 - jsDay;
        const date = new Date(now);
        date.setDate(now.getDate() + mondayOffset + (dayNumber - 1));
        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const getShortDayName = (dayNumber) => {
        const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
        return days[dayNumber - 1];
    };

    const getTodayDayNumber = () => displayWeekDayNumber();

    const toMinutes = (timeStr) => {
        if (!timeStr) return null;
        const normalized = timeStr.toString().trim().substring(0, 5);
        const [h, m] = normalized.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
    };

    const isCurrentLesson = (lesson) => {
        if (horizon === 'semester') {
            if (selectedDate !== toIsoDate(new Date(nowTick))) return false;
        } else if (getTodayDayNumber() !== viewDay) {
            return false;
        }
        const now = new Date(nowTick);
        const nowMin = now.getHours() * 60 + now.getMinutes();
        const start = toMinutes(lesson.start_time);
        const end = toMinutes(lesson.end_time);
        if (start == null || end == null) return false;
        return nowMin >= start && nowMin <= end;
    };

    const todayDay = getTodayDayNumber();
    const selectionReady =
        (scheduleType === 'student' && selectedGroup) ||
        (scheduleType === 'teacher' && selectedTeacher);
    const showIntro = !selectionReady && !introDismissed;

    useEffect(() => {
        document.body.classList.toggle('kkt-schedule-intro', showIntro);
        return () => document.body.classList.remove('kkt-schedule-intro');
    }, [showIntro]);

    useEffect(() => {
        if (!uiReveal) {
            document.body.classList.remove('kkt-schedule-reveal');
            return undefined;
        }
        document.body.classList.add('kkt-schedule-reveal');
        const t = window.setTimeout(() => setUiReveal(false), 1400);
        return () => {
            window.clearTimeout(t);
            document.body.classList.remove('kkt-schedule-reveal');
        };
    }, [uiReveal]);
    const todayIso = toIsoDate(new Date());
    const inPeriod = (iso) => {
        if (!iso) return true;
        if (periodMeta.start && iso < periodMeta.start) return false;
        if (periodMeta.end && iso > periodMeta.end) return false;
        return true;
    };

    useEffect(() => {
        if (rememberChoice) {
            writeSchedulePrefs({ horizon });
        }
        if (horizon === 'semester') {
            setDaysDocked(false);
        }
    }, [horizon, rememberChoice]);

    useEffect(() => {
        if (horizon !== 'semester' || !selectionReady) {
            return undefined;
        }
        const ym = `${calCursor.year}-${String(calCursor.month + 1).padStart(2, '0')}`;
        let url = '';
        if (scheduleType === 'student') {
            url = apiUrl(`/api/public/schedule/students/${selectedGroup}/month/${ym}`);
        } else {
            url = apiUrl(
                `/api/public/schedule/teachers/${encodeURIComponent(selectedTeacher)}/month/${ym}`
            );
        }
        let cancelled = false;
        axios
            .get(url, { timeout: 10000 })
            .then((resp) => {
                if (!cancelled) setMonthDays(resp.data?.days || {});
            })
            .catch(() => {
                if (!cancelled) setMonthDays({});
            });
        return () => {
            cancelled = true;
        };
    }, [horizon, calCursor, selectedGroup, selectedTeacher, scheduleType, selectionReady]);

    useEffect(() => {
        const openFromDock = () => {
            setSettingsOpen(true);
            window.setTimeout(() => {
                const panel = document.querySelector('.schedule-settings-desktop');
                if (panel) {
                    panel.classList.add('is-attention');
                    window.setTimeout(() => panel.classList.remove('is-attention'), 1200);
                }
            }, 40);
        };
        window.addEventListener('kkt-open-settings', openFromDock);
        return () => window.removeEventListener('kkt-open-settings', openFromDock);
    }, []);

    useEffect(() => {
        if (!settingsOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setSettingsOpen(false);
        };
        document.addEventListener('keydown', onKey);
        const prevY = document.body.style.overflowY;
        if (window.matchMedia('(max-width: 767px)').matches) {
            document.body.style.overflowY = 'hidden';
            document.body.classList.add('kkt-settings-open');
        }
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflowY = prevY;
            document.body.classList.remove('kkt-settings-open');
        };
    }, [settingsOpen]);

    useEffect(() => {
        if (settingsOpen) return undefined;
        setDrawerDragX(0);
        setDrawerDragging(false);
        drawerSwipeRef.current = {
            active: false,
            startX: 0,
            startY: 0,
            dx: 0,
            axis: null,
        };
        return undefined;
    }, [settingsOpen]);

    useEffect(
        () => () => {
            if (drawerCloseTimerRef.current) {
                window.clearTimeout(drawerCloseTimerRef.current);
            }
        },
        []
    );

    useEffect(() => {
        const panel = settingsPanelRef.current;
        if (!panel || !settingsOpen) return undefined;

        const swipe = drawerSwipeRef.current;

        const onTouchStart = (e) => {
            if (drawerCloseTimerRef.current) return;
            const t = e.touches[0];
            if (!t) return;
            const hit = e.target && e.target.closest
              ? e.target.closest('button, a, input, textarea, select, label, [role="button"]')
              : null;
            if (hit) return;
            swipe.active = true;
            swipe.startX = t.clientX;
            swipe.startY = t.clientY;
            swipe.dx = 0;
            swipe.axis = null;
        };

        const onTouchMove = (e) => {
            if (!swipe.active) return;
            const t = e.touches[0];
            if (!t) return;
            const dx = t.clientX - swipe.startX;
            const dy = t.clientY - swipe.startY;

            if (swipe.axis == null) {
                if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
                swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
                if (swipe.axis === 'h' && dx > 0) {
                    setDrawerDragging(true);
                }
            }

            if (swipe.axis !== 'h') return;

            const next = Math.max(0, dx);
            swipe.dx = next;
            if (next > 0) {
                setDrawerDragging(true);
                setDrawerDragX(next);
                e.preventDefault();
            } else {
                setDrawerDragX(0);
            }
        };

        const finishSwipeClose = () => {
            const width = panel.offsetWidth || 320;
            setDrawerDragging(false);
            setDrawerDragX(width + 28);
            if (drawerCloseTimerRef.current) {
                window.clearTimeout(drawerCloseTimerRef.current);
            }
            drawerCloseTimerRef.current = window.setTimeout(() => {
                drawerCloseTimerRef.current = null;
                setSettingsOpen(false);
                setDrawerDragX(0);
            }, 220);
        };

        const onTouchEnd = () => {
            if (!swipe.active) return;
            swipe.active = false;
            const dx = swipe.dx;
            const wasHorizontal = swipe.axis === 'h';
            swipe.axis = null;
            swipe.dx = 0;

            if (!wasHorizontal) {
                setDrawerDragging(false);
                setDrawerDragX(0);
                return;
            }

            const width = panel.offsetWidth || 320;
            if (dx > Math.min(96, width * 0.28)) {
                finishSwipeClose();
            } else {
                setDrawerDragging(false);
                setDrawerDragX(0);
            }
        };

        panel.addEventListener('touchstart', onTouchStart, { passive: true });
        panel.addEventListener('touchmove', onTouchMove, { passive: false });
        panel.addEventListener('touchend', onTouchEnd);
        panel.addEventListener('touchcancel', onTouchEnd);

        return () => {
            panel.removeEventListener('touchstart', onTouchStart);
            panel.removeEventListener('touchmove', onTouchMove);
            panel.removeEventListener('touchend', onTouchEnd);
            panel.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [settingsOpen]);

    useEffect(() => {
        if (horizon === 'semester' || !selectionReady) {
            setDaysDocked(false);
            return undefined;
        }
        const el = daysNavRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;

        let hideTimer = null;
        let showTimer = null;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const ratio = entry.intersectionRatio;
                const top = entry.boundingClientRect.top;
                const shouldShow = !entry.isIntersecting || (top < 64 && ratio < 0.35);
                const shouldHide = entry.isIntersecting && ratio > 0.55 && top >= 48;

                if (shouldShow) {
                    if (hideTimer) {
                        window.clearTimeout(hideTimer);
                        hideTimer = null;
                    }
                    if (!showTimer) {
                        showTimer = window.setTimeout(() => {
                            showTimer = null;
                            setDaysDocked((prev) => (prev ? prev : true));
                        }, 60);
                    }
                } else if (shouldHide) {
                    if (showTimer) {
                        window.clearTimeout(showTimer);
                        showTimer = null;
                    }
                    if (!hideTimer) {
                        hideTimer = window.setTimeout(() => {
                            hideTimer = null;
                            setDaysDocked((prev) => (prev ? false : prev));
                        }, 120);
                    }
                }
            },
            {
                root: null,
                rootMargin: '-56px 0px 0px 0px',
                threshold: [0, 0.25, 0.5, 0.75, 1],
            }
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
            if (hideTimer) window.clearTimeout(hideTimer);
            if (showTimer) window.clearTimeout(showTimer);
        };
    }, [selectionReady, horizon]);

    const closeSettings = () => {
        if (typeof document !== 'undefined' && document.activeElement?.blur) {
            document.activeElement.blur();
        }
        if (drawerCloseTimerRef.current) {
            window.clearTimeout(drawerCloseTimerRef.current);
            drawerCloseTimerRef.current = null;
        }
        setDrawerDragging(false);
        setDrawerDragX(0);
        setSettingsOpen(false);
    };

    const handleDateChange = (iso) => {
        if (!iso || iso === selectedDate) return;
        const next = parseIsoDate(iso);
        const prev = parseIsoDate(selectedDate);
        if (next && prev) {
            setDaySlideDir(next > prev ? 1 : -1);
        }
        setSelectedDate(iso);
    };

    const shiftCalendar = (delta) => {
        setCalCursor((prev) => {
            const d = new Date(prev.year, prev.month + delta, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
        });
    };

    const renderDayButtons = (compact = false) =>
        [1, 2, 3, 4, 5, 6].map((day) => (
            <button
                key={day}
                type="button"
                className={`day-btn ${compact ? 'day-btn--compact' : ''} ${selectedDay === day ? 'active' : ''} ${todayDay === day ? 'is-today' : ''}`}
                onClick={() => handleDayChange(day)}
            >
                <span className="day-name">{getShortDayName(day)}</span>
                {!compact ? (
                    <span className="day-number">{todayDay === day ? 'сегодня' : day}</span>
                ) : null}
            </button>
        ));

    const calendarCells = buildMonthCells(calCursor.year, calCursor.month);
    const renderCalendar = () => (
        <div className="sch-cal">
            <div className="sch-cal__nav">
                <button
                    type="button"
                    className="sch-cal__chevron"
                    onClick={() => shiftCalendar(-1)}
                    aria-label="Предыдущий месяц"
                >
                    <i className="bi bi-chevron-left"></i>
                </button>
                <span className="sch-cal__label">
                    {MONTH_NAMES[calCursor.month]} {calCursor.year}
                </span>
                <button
                    type="button"
                    className="sch-cal__chevron"
                    onClick={() => shiftCalendar(1)}
                    aria-label="Следующий месяц"
                >
                    <i className="bi bi-chevron-right"></i>
                </button>
                <button
                    type="button"
                    className="sch-cal__today"
                    onClick={() => {
                        const now = new Date();
                        setCalCursor({ year: now.getFullYear(), month: now.getMonth() });
                        handleDateChange(todayIso);
                    }}
                >
                    Сегодня
                </button>
            </div>
            <div className="sch-cal__weekdays">
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((label) => (
                    <span key={label}>{label}</span>
                ))}
            </div>
            <div className="sch-cal__grid">
                {calendarCells.map((cell) => {
                    const count = monthDays[cell.iso] || 0;
                    const outOfPeriod = !inPeriod(cell.iso);
                    const isSun = isoWeekdayFromIso(cell.iso) === 7;
                    return (
                        <button
                            key={cell.iso}
                            type="button"
                            className={[
                                'sch-cal__day',
                                cell.inMonth ? '' : 'is-out',
                                cell.iso === todayIso ? 'is-today' : '',
                                cell.iso === selectedDate ? 'is-selected' : '',
                                outOfPeriod ? 'is-period-out' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => handleDateChange(cell.iso)}
                            disabled={isSun}
                        >
                            <span className="sch-cal__num">{cell.day}</span>
                            {count > 0 && !isSun ? (
                                <span className="sch-cal__dots">
                                    {Array.from({ length: Math.min(count, 3) }).map((_, idx) => (
                                        <i key={idx} />
                                    ))}
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const renderSettings = (variant) => (
        <div className={`schedule-selectors schedule-selectors--${variant}`}>
            <div className="selector-container selector-container--type">
                <label className="selector-label">
                    <i className="bi bi-sliders"></i>
                    Кто смотрит расписание
                </label>
                <div className="type-switch" role="group" aria-label="Тип расписания">
                    <button
                        type="button"
                        className={`type-chip ${scheduleType === 'student' ? 'active' : ''}`}
                        disabled={loadingData}
                        onClick={() => {
                            setScheduleType('student');
                            setSelectedTeacher('');
                            setSchedule([]);
                        }}
                    >
                        <i className="bi bi-mortarboard"></i>
                        Студент
                    </button>
                    <button
                        type="button"
                        className={`type-chip ${scheduleType === 'teacher' ? 'active' : ''}`}
                        disabled={loadingData}
                        onClick={() => {
                            setScheduleType('teacher');
                            setSelectedGroup('');
                            setSchedule([]);
                        }}
                    >
                        <i className="bi bi-person-workspace"></i>
                        Преподаватель
                    </button>
                </div>
            </div>

            <div className="selector-container selector-container--type">
                <label className="selector-label">
                    <i className="bi bi-calendar3"></i>
                    Какой период показать
                </label>
                <div className="type-switch" role="group" aria-label="Период расписания">
                    <button
                        type="button"
                        className={`type-chip ${horizon === 'week' ? 'active' : ''}`}
                        onClick={() => setHorizon('week')}
                    >
                        <i className="bi bi-calendar-week"></i>
                        На неделю
                    </button>
                    <button
                        type="button"
                        className={`type-chip ${horizon === 'semester' ? 'active' : ''}`}
                        onClick={() => setHorizon('semester')}
                    >
                        <i className="bi bi-calendar-month"></i>
                        На семестр
                    </button>
                </div>
            </div>

            {scheduleType === 'student' && (
                <div className="selector-container">
                    <SearchableSelector
                        value={selectedGroup}
                        onChange={setSelectedGroup}
                        options={groups}
                        placeholder="Начните вводить группу..."
                        disabled={loadingData}
                        icon="bi-people"
                        label="Группа"
                    />
                    <div className="remember-choice">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={rememberChoice}
                                onChange={(e) => setRememberChoice(e.target.checked)}
                            />
                            <span>Запомнить мой выбор</span>
                        </label>
                    </div>
                </div>
            )}

            {scheduleType === 'teacher' && (
                <div className="selector-container">
                    <SearchableSelector
                        value={selectedTeacher}
                        onChange={setSelectedTeacher}
                        options={teachers}
                        placeholder="Начните вводить ФИО..."
                        disabled={loadingData}
                        icon="bi-person"
                        label="Преподаватель"
                    />
                    <div className="remember-choice">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={rememberChoice}
                                onChange={(e) => setRememberChoice(e.target.checked)}
                            />
                            <span>Запомнить мой выбор</span>
                        </label>
                    </div>
                </div>
            )}

            {selectionReady ? (
                <div className="load-button-container">
                    <button
                        className="load-schedule-btn"
                        onClick={() => {
                            loadSchedule();
                            closeSettings();
                        }}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <SmileLoader size="xs" />
                                Загрузка...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-calendar-week"></i>
                                Показать расписание
                            </>
                        )}
                    </button>
                </div>
            ) : null}
        </div>
    );

    return (
        <div
            className={`schedule-page ${selectionReady ? 'schedule-page--ready' : ''} ${daysDocked ? 'schedule-page--days-docked' : ''} ${showIntro ? 'schedule-page--intro' : ''} ${uiReveal ? 'schedule-page--reveal' : ''}`}
        >
            {!showIntro ? (
            <div className="schedule-toolbar">
                {schedule.length > 0 && (
                    <button
                        className="header-btn screenshot-btn"
                        onClick={takeScreenshot}
                        disabled={screenshotLoading}
                        title="Скриншот"
                    >
                        <i className={`bi bi-camera ${screenshotLoading ? 'spin' : ''}`}></i>
                        <span className="btn-tooltip">
                            {screenshotLoading ? 'Создаём...' : 'Скриншот'}
                        </span>
                    </button>
                )}

                <button
                    className="header-btn refresh-btn"
                    onClick={loadSchedule}
                    disabled={loading || !selectionReady}
                    title="Обновить"
                >
                    <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
                    <span className="btn-tooltip">Обновить</span>
                </button>

                <button
                    className="header-btn reset-btn"
                    onClick={resetSelection}
                    title="Сбросить"
                >
                    <i className="bi bi-x-lg"></i>
                    <span className="btn-tooltip">Сбросить</span>
                </button>
            </div>
            ) : null}

            <div className="schedule-layout">
                <div className="schedule-layout__main">
                    {selectionReady ? (
                        horizon === 'semester' ? (
                            <div className="sch-cal-wrap" ref={daysNavRef}>
                                {renderCalendar()}
                            </div>
                        ) : (
                            <nav className="days-navigation" aria-label="Дни недели" ref={daysNavRef}>
                                <div className="days-container">{renderDayButtons(false)}</div>
                            </nav>
                        )
                    ) : null}

                    <main className="schedule-main">
                        <div className="schedule-container" ref={scheduleRef}>
                            {showIntro ? (
                                <div className="welcome-state welcome-state--buddy">
                                    <WelcomeBuddy
                                        groups={groups}
                                        teachers={teachers}
                                        loadingData={loadingData}
                                        settingsOpen={settingsOpen}
                                        showVkLink={Boolean(user && user.vk_linked === false)}
                                        onComplete={({ type, group, teacher }) => {
                                            const nextGroup = type === 'student' ? group || '' : '';
                                            const nextTeacher = type === 'teacher' ? teacher || '' : '';
                                            setScheduleType(type);
                                            setSelectedGroup(nextGroup);
                                            setSelectedTeacher(nextTeacher);
                                            setRememberChoice(true);
                                            setIntroDismissed(true);
                                            setSettingsOpen(false);
                                            setUiReveal(true);
                                            writeSchedulePrefs({
                                                type,
                                                group: nextGroup,
                                                teacher: nextTeacher,
                                                horizon,
                                            });
                                        }}
                                    />
                                </div>
                            ) : !scheduleType ? (
                                <div className="welcome-state">
                                    <div className="welcome-icon welcome-icon--image">
                                        <img
                                            src={timeIcon}
                                            alt=""
                                            width={96}
                                            height={96}
                                            decoding="async"
                                        />
                                    </div>
                                    <h3>Добро пожаловать</h3>
                                    <p>Выберите, для кого открыть расписание</p>
                                    <button
                                        type="button"
                                        className="retry-btn schedule-open-settings"
                                        onClick={() => setSettingsOpen(true)}
                                    >
                                        <i className="bi bi-sliders"></i>
                                        Открыть настройки
                                    </button>
                                </div>
                            ) : scheduleType === 'student' && !selectedGroup ? (
                                <div className="welcome-state">
                                    <div className="welcome-icon">
                                        <i className="bi bi-people"></i>
                                    </div>
                                    <h3>Выберите группу</h3>
                                    <p>Начните вводить название или выберите из списка</p>
                                    <button
                                        type="button"
                                        className="retry-btn schedule-open-settings"
                                        onClick={() => setSettingsOpen(true)}
                                    >
                                        <i className="bi bi-sliders"></i>
                                        Открыть настройки
                                    </button>
                                    {loadingData && (
                                        <div className="loading-small">
                                            <StickmanRunner size="sm" />
                                            <span>Загрузка списка групп...</span>
                                        </div>
                                    )}
                                </div>
                            ) : scheduleType === 'teacher' && !selectedTeacher ? (
                                <div className="welcome-state">
                                    <div className="welcome-icon">
                                        <i className="bi bi-person"></i>
                                    </div>
                                    <h3>Выберите преподавателя</h3>
                                    <p>Начните вводить ФИО или выберите из списка</p>
                                    <button
                                        type="button"
                                        className="retry-btn schedule-open-settings"
                                        onClick={() => setSettingsOpen(true)}
                                    >
                                        <i className="bi bi-sliders"></i>
                                        Открыть настройки
                                    </button>
                                    {loadingData && (
                                        <div className="loading-small">
                                            <StickmanRunner size="sm" />
                                            <span>Загрузка списка преподавателей...</span>
                                        </div>
                                    )}
                                </div>
                            ) : loading ? (
                                <div className="loading-state network-state">
                                    <SmileLoader size="md" />
                                    <h3>Загружаем расписание</h3>
                                    <p>Ещё чуть-чуть…</p>
                                </div>
                            ) : error ? (
                                <div
                                    className={`error-state network-state ${
                                        error === 'Проблемы с сетью' ? 'network-state--offline' : ''
                                    }`}
                                >
                                    {error === 'Проблемы с сетью' ? (
                                        <SignalLoader size="md" />
                                    ) : (
                                        <i className="bi bi-exclamation-triangle"></i>
                                    )}
                                    <h3>
                                        {error === 'Проблемы с сетью'
                                            ? 'Проблемы с сетью'
                                            : 'Не удалось загрузить'}
                                    </h3>
                                    <p>
                                        {error === 'Проблемы с сетью'
                                            ? 'API недоступен или пропал интернет. Попробуйте ещё раз.'
                                            : error}
                                    </p>
                                    <button onClick={loadSchedule} className="retry-btn">
                                        <i className="bi bi-arrow-clockwise"></i>
                                        Попробовать снова
                                    </button>
                                </div>
                            ) : (
                                schedule.length === 0 ? (
                                        <div className="empty-state network-state">
                                            <CupSteamLoader size="md" />
                                            <h3>{scheduleType === 'teacher' ? 'Методический день' : 'Свободный день'}</h3>
                                            <p>
                                                {horizon === 'semester'
                                                    ? `На ${formatRuDate(selectedDate)} занятий нет`
                                                    : `На ${getDayName(selectedDay).toLowerCase()} занятий нет`}
                                            </p>
                                            <button
                                                onClick={loadSchedule}
                                                className="retry-btn"
                                                style={{ marginTop: '1rem' }}
                                            >
                                                <i className="bi bi-arrow-clockwise"></i>
                                                Обновить
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="lessons-list">
                                            <div className="schedule-summary">
                                                <h2 className="schedule-summary__title">
                                                    {scheduleType === 'student'
                                                        ? selectedGroup
                                                        : selectedTeacher}
                                                </h2>
                                                <div className="schedule-summary__day-slot">
                                                    <p
                                                        key={horizon === 'semester' ? selectedDate : selectedDay}
                                                        className={`schedule-summary__day ${
                                                            daySlideDir > 0
                                                                ? 'is-from-right'
                                                                : daySlideDir < 0
                                                                  ? 'is-from-left'
                                                                  : 'is-static'
                                                        }`}
                                                    >
                                                        <span className="schedule-summary__day-name">
                                                            {horizon === 'semester'
                                                                ? (isoWeekdayFromIso(selectedDate) === 7
                                                                    ? 'Воскресенье'
                                                                    : getDayName(isoWeekdayFromIso(selectedDate)))
                                                                : getDayName(selectedDay)}
                                                        </span>
                                                        <span className="schedule-summary__day-date">
                                                            {horizon === 'semester'
                                                                ? formatRuDate(selectedDate)
                                                                : getDateForDay(selectedDay)}
                                                        </span>
                                                    </p>
                                                </div>
                                                <p className="schedule-summary__count">
                                                    {schedule.length}{' '}
                                                    {schedule.length === 1
                                                        ? 'пара'
                                                        : schedule.length < 5
                                                          ? 'пары'
                                                          : 'пар'}
                                                </p>
                                            </div>

                                    <div className={`lessons-body${switching ? ' is-switching' : ''}`}>
                                    {switching ? (
                                        <div className="lessons-switch-overlay" aria-live="polite">
                                            <SmileLoader size="md" />
                                        </div>
                                    ) : null}
                                    {schedule.map((lesson, index) => {
                                        const lessonNumber = Number(lesson.pair_num) > 0
                                            ? Number(lesson.pair_num)
                                            : getLessonNumberByTime(
                                                lesson.start_time,
                                                viewDay
                                            );
                                        const displayNumber =
                                            lessonNumber !== null ? lessonNumber : index + 1;
                                        const current = isCurrentLesson(lesson);
                                        const start = lesson.start_time?.substring(0, 5);
                                        const end = lesson.end_time?.substring(0, 5);
                                        const room = lesson.room || '';
                                        const isDist = isDistanceRoom(room);

                                        return (
                                            <article
                                                key={lesson.id || index}
                                                className={`lesson-item ${current ? 'is-current' : ''}`}
                                                aria-current={current ? 'true' : undefined}
                                            >
                                                <span className="lesson-item__art" aria-hidden="true">
                                                    <span className="lesson-item__plate" />
                                                    <span className="lesson-item__num">{displayNumber}</span>
                                                </span>
                                                {current ? (
                                                    <span className="lesson-now" aria-label="Идёт сейчас">сейчас</span>
                                                ) : null}

                                                <div className="lesson-content">
                                                    <div className="lesson-top">
                                                        <h3 className="lesson-subject">
                                                            {lesson.subject}
                                                            {lesson.is_override ? (
                                                                <span className="now-pill now-pill--change">замена</span>
                                                            ) : null}
                                                        </h3>
                                                        <p
                                                            className="lesson-time"
                                                            aria-label={`Пара ${displayNumber}, ${start}–${end}`}
                                                        >
                                                            <span className="lesson-time__start">{start}</span>
                                                            <span className="lesson-time__sep" aria-hidden="true">
                                                                –
                                                            </span>
                                                            <span className="lesson-time__end">{end}</span>
                                                        </p>
                                                    </div>

                                                    <div className="lesson-details">
                                                        <div className="detail-chip">
                                                            <i className="bi bi-person"></i>
                                                            <span>{lesson.teacher_full_name}</span>
                                                        </div>

                                                        {isDist ? (
                                                            <button
                                                                type="button"
                                                                className="detail-chip detail-chip--room detail-chip--dist"
                                                                onClick={() => {
                                                                    if (typeof openExternal === 'function') {
                                                                        openExternal(
                                                                            LINKS.eo,
                                                                            'электронное обучение'
                                                                        );
                                                                    } else {
                                                                        window.open(
                                                                            LINKS.eo,
                                                                            '_blank',
                                                                            'noopener,noreferrer'
                                                                        );
                                                                    }
                                                                }}
                                                                title="Перейти к электронному обучению"
                                                            >
                                                                <i className="bi bi-laptop"></i>
                                                                <span>{room}</span>
                                                            </button>
                                                        ) : room ? (
                                                            <button
                                                                type="button"
                                                                className="detail-chip detail-chip--room"
                                                                onClick={() => setMapRoom(room)}
                                                                title="Показать на карте"
                                                            >
                                                                <i className="bi bi-door-open"></i>
                                                                <span>{room}</span>
                                                            </button>
                                                        ) : (
                                                            <div className="detail-chip detail-chip--muted">
                                                                <i className="bi bi-door-closed"></i>
                                                                <span>Аудитория не указана</span>
                                                            </div>
                                                        )}

                                                        {lesson.group_id &&
                                                            scheduleType === 'teacher' && (
                                                                <div className="detail-chip">
                                                                    <i className="bi bi-people"></i>
                                                                    <span>{lesson.group_id}</span>
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}
                                    </div>
                                        </div>
                                    )
                            )}
                        </div>
                    </main>

                    {!showIntro ? <BannerCarousel placement="schedule" /> : null}
                </div>

                {!showIntro ? (
                <aside className="schedule-settings-desktop" aria-label="Настройки расписания">
                    {renderSettings('desktop')}
                </aside>
                ) : null}
            </div>

            {selectionReady && horizon === 'week' ? (
                <div
                    className={`days-dock ${daysDocked ? 'is-visible' : ''}`}
                    aria-hidden={!daysDocked}
                >
                    <div className="days-container days-container--dock">
                        {renderDayButtons(true)}
                    </div>
                </div>
            ) : null}

            <RoomMapModal
                open={Boolean(mapRoom)}
                room={mapRoom}
                onClose={() => setMapRoom(null)}
            />

            <div
                className={`settings-drawer ${settingsOpen ? 'is-open' : ''} ${drawerDragging ? 'is-dragging' : ''}`}
                aria-hidden={!settingsOpen}
                style={{
                    '--drawer-drag': `${drawerDragX}px`,
                    '--drawer-dim': String(
                        Math.max(
                            0,
                            1 - drawerDragX / Math.max(1, settingsPanelRef.current?.offsetWidth || 320)
                        )
                    ),
                }}
            >
                <button
                    type="button"
                    className="settings-drawer__backdrop"
                    aria-label="Закрыть настройки"
                    onClick={closeSettings}
                />
                <aside
                    ref={settingsPanelRef}
                    className="settings-drawer__panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Настройки расписания"
                >
                    <div className="settings-drawer__head">
                        <h3>
                            <i className="bi bi-sliders"></i>
                            Настройки
                        </h3>
                        <button
                            type="button"
                            className="settings-drawer__close"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={closeSettings}
                            aria-label="Закрыть"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    {renderSettings('drawer')}
                </aside>
            </div>
        </div>
    );
};

export default Schedule;
